import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import { ThemeContext } from 'styled-components';
import { List } from 'immutable';
import { getBreakpointFromManor } from 'ctm-components';
import { Table, TableRow, TableCell, TableBody } from '@comparethemarketau/manor-table';
import { TableSectionHeader } from '../common/table_section_header';
import { ProductTableRow } from '../common/product_table_row';
import { HtmlCell } from '../cell/html_cell';
import { PlainTextCell } from '../cell/plain_text_cell';
import { SuitableCell } from '../cell/suitable_cell';
import { AdditionalExcessCell } from '../cell/additional_excess_cell';
import { DiscountOfferTermsLink } from '../../products/products/common/discount_offer_terms_link';

import { isDefaultTextDiscountOffer } from '../../products/products/common/discount_offer';

const useStyles = createUseStyles((theme) => ({
    defaultCell: {
        padding: 0
    },
    defaultRow: {
        extend: ({ breakpoint }) => {
            if (breakpoint === 'md' || breakpoint === 'lg') {
                return {
                    borderLeft: `0.2rem solid ${theme.colors.grey300}`,
                    borderRight: `0.2rem solid ${theme.colors.grey300}`,
                    borderTop: `0.2rem solid ${theme.colors.grey300}`
                };
            }
            return {
                borderLeft: `0.1rem solid ${theme.colors.grey300}`,
                borderRight: `0.1rem solid ${theme.colors.grey300}`,
                borderTop: `0.1rem solid ${theme.colors.grey300}`
            };
        }
    }
}));

const ComparingProductsKeyInformation = (props) => {
    const { selectedProducts, cellClassName, columnCount, stackDisplayMode } = props;
    const breakpoint = getBreakpointFromManor();
    const theme = useContext(ThemeContext);
    const classes = useStyles({ theme, breakpoint });

    return (
        <div className={'test-policyComparison-products-keyInformation'}>
            <Table ariaLabel="Products Key Information table" ariaDescribedby="IDREF">
                <TableBody>
                    <TableRow className={classes.defaultRow}>
                        <TableCell className={classes.defaultCell} colspan={columnCount}><TableSectionHeader isKeyInformation /></TableCell>
                    </TableRow>
                    <ProductTableRow
                        key={'Special_Offer'}
                        title={'Special offer'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const text = product.getIn(['productMeta', 'discount', 'discountOffer']);
                                const id = product.getIn(['productMeta', 'id']);
                                const providerCode = product.getIn(['providerMeta', 'code']);
                                return (
                                    <PlainTextCell key={`specialOffer-${id}`}
                                        plainText={(
                                            <span>
                                                {isDefaultTextDiscountOffer(providerCode) && text}
                                                <DiscountOfferTermsLink quote={product}>
                                                    {isDefaultTextDiscountOffer(providerCode) ? ' More info' : text}
                                                </DiscountOfferTermsLink>
                                            </span>
                                        )} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Special_conditions'}
                        title={'Special conditions'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const specialConditions = product.getIn(['productMeta', 'specialConditions']);
                                const id = product.getIn(['productMeta', 'id']);
                                return (
                                    <HtmlCell htmlText={specialConditions} key={`specialConditions-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Is_this_product_suitable_for_me'}
                        title={'Is this product suitable for me?'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const id = product.getIn(['productMeta', 'id']);
                                return (
                                    <SuitableCell product={product} key={`suitable-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Excess'}
                        title={'Excess'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const excess = product.getIn(['productMeta', 'excess']);
                                const glassExcess = product.getIn(['productMeta', 'glassExcess']);

                                const id = product.getIn(['productMeta', 'id']);

                                return (
                                    <PlainTextCell plainText={
                                        (
                                            <>
                                                <div>{`Basic Excess - $${excess}`}</div>
                                                {glassExcess && <div>{`Glass Excess - $${glassExcess}`}</div>}
                                            </>
                                        )
                                    }
                                    key={`specialOffer-${id}`} />
                                );
                            })
                        }
                    />
                    <ProductTableRow
                        key={'Additional_Excess'}
                        title={'Additional excess'}
                        stackDisplayMode={stackDisplayMode}
                        columnCount={columnCount}
                        cellClassName={cellClassName}
                        cells={
                            selectedProducts.map((product) => {
                                const additionalExcesses = product.getIn(['productMeta', 'additionalExcesses']);
                                const id = product.getIn(['productMeta', 'id']);
                                return (
                                    <AdditionalExcessCell additionalExcesses={additionalExcesses} key={`additionalExcesses-${id}`} />
                                );
                            })
                        }
                    />
                </TableBody>
            </Table>
        </div>
    );
};

ComparingProductsKeyInformation.propTypes = {
    selectedProducts: PropTypes.instanceOf(List),
    cellClassName: PropTypes.string,
    stackDisplayMode: PropTypes.bool,
    columnCount: PropTypes.number
};

export { ComparingProductsKeyInformation };
